import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import {
  VisualEffect,
  type VisualEffectRef,
  type ShockwaveEffectProps,
  type EmojiBurstEffectProps,
} from "@pineseed/gui/component/visual-effect";
import { AvatarDome } from "./AvatarDome";
import { ChatBubble } from "./ChatBubble";
import { BubbleProvider, useBubble } from "./BubbleContext";

export interface AvatarChatConfig {
  size: number | string;
  facesBasePath: string;
  verticalRange: { min: number; max: number };
  shockwave: ShockwaveEffectProps;
  emojiBurst: EmojiBurstEffectProps;
}

export interface AvatarChatHandle {
  show: (content: React.ReactNode) => void;
  close: () => void;
  shockwave: () => void;
  burst: (emoji: string) => void;
}

export interface AvatarChatProps {
  config: AvatarChatConfig;
  onAvatarClick: (event: MouseEvent) => void;
}

const AvatarChatInner = forwardRef<AvatarChatHandle, AvatarChatProps>(
  ({ config, onAvatarClick }, ref) => {
    const domeRef = useRef<HTMLDivElement>(null);
    const visualEffectRef = useRef<VisualEffectRef>(null);
    const mouseXRef = useRef<number>(0);
    const {
      triggerBubble,
      activeBubble,
      closingBubble,
      setActiveBubble,
      closeBubble,
      handleAnimationEnd,
      isExpanded,
      collapseBubble,
    } = useBubble();

    useImperativeHandle(
      ref,
      () => ({
        show: (content: React.ReactNode) => {
          triggerBubble(
            content,
            mouseXRef.current < window.innerWidth / 2 ? "left" : "right",
          );
        },
        close: closeBubble,
        shockwave: () => {
          visualEffectRef.current?.trigger();
        },
        burst: (emoji: string) => {
          visualEffectRef.current?.trigger({ emoji });
        },
      }),
      [triggerBubble, closeBubble],
    );

    // Track mouse position and close bubble when clicking outside or pressing Escape
    useEffect(() => {
      const handleMouseMove = (event: MouseEvent) => {
        mouseXRef.current = event.clientX;
      };

      const handleClickOutside = (event: MouseEvent) => {
        if (!activeBubble || !domeRef.current) return;

        // Expanded mode click-outside is handled by the backdrop in ChatBubble
        if (isExpanded) return;

        const target = event.target as Node;
        const glassDomeContainer = domeRef.current;

        if (!glassDomeContainer.contains(target)) {
          setActiveBubble(null);
        }
      };

      const handleEscapeKey = (event: KeyboardEvent) => {
        if (event.key === "Escape" && activeBubble) {
          if (isExpanded) {
            collapseBubble();
          } else {
            setActiveBubble(null);
          }
        }
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscapeKey);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mousedown", handleClickOutside);
        document.removeEventListener("keydown", handleEscapeKey);
      };
    }, [activeBubble, setActiveBubble, isExpanded, collapseBubble]);

    return (
      <div
        ref={domeRef}
        className="avatar-dome-container flex justify-center"
        style={{
          position: "relative",
          overflow: "visible",
        }}
      >
        <VisualEffect
          ref={visualEffectRef}
          shockwaveProps={config.shockwave}
          emojiBurstProps={config.emojiBurst}
        >
          <AvatarDome
            useFaceTracker
            facesBasePath={config.facesBasePath}
            size={config.size}
            onClick={onAvatarClick}
          />
          {closingBubble && (
            <ChatBubble
              bubble={closingBubble}
              isClosing={true}
              onClose={closeBubble}
              onAnimationEnd={handleAnimationEnd}
            />
          )}
          {activeBubble && (
            <ChatBubble
              bubble={activeBubble}
              isClosing={false}
              onClose={closeBubble}
              onAnimationEnd={handleAnimationEnd}
            />
          )}
        </VisualEffect>
      </div>
    );
  },
);

export const AvatarChat = forwardRef<AvatarChatHandle, AvatarChatProps>(
  (props, ref) => (
    <BubbleProvider verticalRange={props.config.verticalRange}>
      <AvatarChatInner ref={ref} {...props} />
    </BubbleProvider>
  ),
);
